import { observer } from 'mobx-react-lite';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { BrowserRouter, useLocation, useNavigate } from 'react-router-dom';
import { Amplify } from 'aws-amplify';
import { Authenticator, useAuthenticator } from '@aws-amplify/ui-react';
import amplifyConfig from '@/config/amplify';

import UserContext from '@/screens/app/UserContext';
import AppWrapper from '@/screens/app/AppWrapper';
import ErrorBoundary from '@/components/shared/ErrorBoundary';
import AuthGatePanel from '@/components/auth/AuthGatePanel';
import CookieConsent from '@/components/consent/CookieConsent';
import LanguageSelector from '@/components/language/LanguageSelector';
import { AUTH_SIGN_UP_ENABLED, authComponents, authFormFields, authSocialProviders } from '@/config/authUi';
import { PENDING_INVITATION_TOKEN_KEY } from '@/config/invitationStorage';

Amplify.configure(amplifyConfig);

const PUBLIC_ROUTES = ['/accept-invitation'];

/**
 * After Cognito Hosted UI / social OAuth, Amplify returns to `/` (callback URL), not the
 * invite deep link. Restore `/accept-invitation?token=…` from sessionStorage when present.
 */
function useRestorePendingInvitation(): void {
  const { authStatus } = useAuthenticator((context) => [context.authStatus]);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (authStatus !== 'authenticated') return;
    if (location.pathname.startsWith('/accept-invitation')) return;

    const token = sessionStorage.getItem(PENDING_INVITATION_TOKEN_KEY);
    if (!token) return;

    navigate(`/accept-invitation?token=${encodeURIComponent(token)}`, { replace: true });
  }, [authStatus, location.pathname, navigate]);
}

const AppContent = observer(() => {
  const location = useLocation();
  const { authStatus } = useAuthenticator((context) => [context.authStatus]);
  useTranslation('auth');
  useRestorePendingInvitation();

  const isPublicRoute = PUBLIC_ROUTES.some((route) => location.pathname.startsWith(route));
  const isAuthChecking = authStatus === 'configuring';
  const isAuthenticated = authStatus === 'authenticated';

  if (isPublicRoute) {
    return (
      <UserContext>
        <AppWrapper />
      </UserContext>
    );
  }

  if (isAuthChecking) {
    return (
      <div className="auth-shell auth-shell--checking" aria-busy="true" aria-live="polite">
        <div className="auth-shell__checking-spinner" aria-hidden="true" />
      </div>
    );
  }

  const authenticator = (
    <Authenticator
      hideSignUp={!AUTH_SIGN_UP_ENABLED}
      socialProviders={authSocialProviders}
      components={authComponents}
      formFields={authFormFields}
    >
      <UserContext>
        <AppWrapper />
      </UserContext>
    </Authenticator>
  );

  return (
    <div className={`auth-shell${isAuthenticated ? ' auth-shell--app' : ' auth-shell--gate'}`}>
      {!isAuthenticated && <AuthGatePanel />}
      <div className={isAuthenticated ? 'auth-shell__app-slot' : 'auth-shell__form-column'}>
        {!isAuthenticated && (
          <LanguageSelector id="auth-language-mobile" className="language-selector--auth-mobile" size="compact" />
        )}
        {isAuthenticated ? authenticator : <div className="auth-form-card">{authenticator}</div>}
      </div>
    </div>
  );
});

const App = observer(() => {
  return (
    <ErrorBoundary>
      <Authenticator.Provider>
        <BrowserRouter>
          <AppContent />
          <CookieConsent />
        </BrowserRouter>
      </Authenticator.Provider>
    </ErrorBoundary>
  );
});

export default App;
